import React, { useState, useEffect } from 'react';
import { Box, Typography } from '@mui/material';
import { useKeycloak } from '@react-keycloak/web';
import { 
  AgentWizard, 
  PrebuiltAgentWizard, 
  AgentStatusOverview 
} from '../AgentStudio/CreatingAgents';
import { 
  AgentOverview, 
  DeleteAgentDialog 
} from '../AgentStudio/ManagingAgents';
import { getAgentList, deleteAgent } from '../api/services/agent-studio/AgentManagement';
import { validateApiKey } from '../api/services/agent-studio/AgentFeatures';

const AgentStudio = () => {
  const { keycloak } = useKeycloak();
  
  const [showWizard, setShowWizard] = useState(false);
  const [showTemplateWizard, setShowTemplateWizard] = useState(false);
  const [activeAgents, setActiveAgents] = useState([]);
  const [agentConfig, setAgentConfig] = useState(null);
  const [activeStep, setActiveStep] = useState(0);
  const [isLoading, setIsLoading] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [agentToDelete, setAgentToDelete] = useState(null);
  const [apiKey, setApiKey] = useState(null);

  const fetchAgents = async () => {
    setIsLoading(true);
    try {
      await keycloak.updateToken(30);
      const token = keycloak.token;
      const response = await getAgentList(token);
      
      if (response.success && response.data) {
        const agents = Array.isArray(response.data.agents) ? response.data.agents : response.data;
        setActiveAgents(agents);
      }
    } catch (error) {
      console.error("Error fetching agents:", error);
      setActiveAgents([]);
    } finally {
      setIsLoading(false);
    }
  };

  const initializeApiKey = async () => {
    const storedApiKey = localStorage.getItem('api_key');
    if (storedApiKey) {
      try {
        const result = await validateApiKey(storedApiKey);
        if (result && result.is_valid) {
          setApiKey(storedApiKey);
        }
      } catch (error) {
        console.error('Error validating API key:', error);
      }
    }
  };

  useEffect(() => {
    if (keycloak.authenticated) {
      fetchAgents();
      initializeApiKey();
    } else {
      setActiveAgents([]);
    }
  }, [keycloak.authenticated]);

  const handleStartAgent = () => {
    setShowWizard(true);
    setShowTemplateWizard(false);
  };
  
  const handleExploreTemplates = () => {
    setShowTemplateWizard(true);
    setShowWizard(false);
  };

  const handleCompleteAgent = (agentConfig) => {
    if (agentConfig.apiResponse) {
      fetchAgents();
    }
    
    setShowWizard(false);
    setShowTemplateWizard(false);
    setAgentConfig(null);
    setActiveStep(0);
  };

  const handleAgentUpdate = (config, step) => {
    setAgentConfig(config);
    setActiveStep(step);
  };

  const handleDeleteClick = (agent) => {
    setAgentToDelete(agent);
    setDeleteDialogOpen(true);
  };

  const handleDeleteConfirm = async () => {
    if (!agentToDelete) return;
    
    setIsLoading(true);
    try {
      await keycloak.updateToken(30);
      const token = keycloak.token;
      
      await deleteAgent(token, agentToDelete.id);
      await fetchAgents();
    } catch (error) {
      console.error("Error deleting agent:", error);
    } finally {
      setDeleteDialogOpen(false);
      setAgentToDelete(null);
      setIsLoading(false);
    }
  };

  const handleDeleteCancel = () => {
    setDeleteDialogOpen(false);
    setAgentToDelete(null);
  };

  const handleCancelWizard = () => {
    setShowWizard(false);
    setShowTemplateWizard(false);
    setAgentConfig(null);
    setActiveStep(0);
  };

  const renderPageHeader = () => {
    const title = showWizard ? 'Build Custom Agent' : 'Agent Studio';
    
    return (
      <Box sx={{ 
        display: 'flex', 
        alignItems: 'flex-start', 
        justifyContent: 'flex-start',
        mb: 3,
        gap: 10
      }}>
        <Typography variant={showWizard ? "h5" : "h4"}>
          {title}
        </Typography>
        
        {agentConfig && (showWizard || (!showWizard && !showTemplateWizard)) && (
          <Box sx={{ flexGrow: 1, maxWidth: '80%' }}>
            <AgentStatusOverview 
              agentConfig={agentConfig}
              activeStep={activeStep}
            />
          </Box>
        )}
      </Box>
    );
  };

  const renderMainContent = () => {
    if (showWizard) {
      return (
        <AgentWizard 
          onComplete={handleCompleteAgent} 
          onUpdateAgent={handleAgentUpdate}
          onCancel={handleCancelWizard}
        />
      );
    }
    
    if (showTemplateWizard) {
      return (
        <PrebuiltAgentWizard 
          onComplete={handleCompleteAgent}
          onCancel={handleCancelWizard}
        />
      );
    }
    
    return (
      <AgentOverview 
        onLaunchAgent={handleStartAgent}
        onExploreTemplates={handleExploreTemplates}
        activeAgents={activeAgents}
        onRefresh={fetchAgents}
        onDelete={handleDeleteClick}
        isLoading={isLoading}
      />
    );
  };

  return (
    <Box sx={{ p: 3 }}>
      {renderPageHeader()}
      {renderMainContent()}
      
      <DeleteAgentDialog
        open={deleteDialogOpen}
        agent={agentToDelete}
        onClose={handleDeleteCancel}
        onConfirm={handleDeleteConfirm}
      />
    </Box>
  );
};

export default AgentStudio; 